// client/src/components/campaign/CampaignEmailPanel.jsx
//
// The email channel for a marketing campaign (On-Screen 2.0). Lists sends
// with open/click stats and a composer (subject / body / audience /
// schedule). Sends drain through the shared outreach pipeline.

import React, { useCallback, useEffect, useState } from 'react';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';
import {
  listCampaignEmails, createCampaignEmail, queueCampaignEmail, previewAudience,
  EMAIL_AUDIENCES, EMAIL_STATUS_LABELS,
} from '../../services/campaignEmailApi';

// Inline hex pairs [background, text] rather than Tailwind bg-/text- utility
// classes — see the identical note in CampaignList.jsx: the desktop shell
// forces an inherited white text color inside .tenant-content at >=1024px,
// which silently wins over these utility classes and renders the pill
// white-on-white. An inline style is a direct declaration on the element
// itself, so it can't be shadowed by an inherited ancestor color.
const STATUS_SURFACE = {
  draft: ['#f1f5f9', '#334155'],
  scheduled: ['#fef3c7', '#92400e'],
  sending: ['#dbeafe', '#1e40af'],
  paused: ['#ffedd5', '#9a3412'],
  sent: ['#d1fae5', '#065f46'],
};

export default function CampaignEmailPanel({ tenantId, campaignId, eventId }) {
  const { role } = getSessionLocal();
  const canSend = can(role, 'events', 'update') || can(role, 'posting', 'schedule');

  const [rows, setRows] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [composing, setComposing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCampaignEmails(tenantId, campaignId);
      setRows(res.data || []);
      setEnabled(res.channelEnabled !== false);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  }, [tenantId, campaignId]);

  useEffect(() => { load(); }, [load]);

  const queue = async (id) => {
    try { await queueCampaignEmail(tenantId, id); load(); }
    catch (err) { setError(err?.response?.data?.message || 'Could not queue'); }
  };

  return (
    <section className="rounded-lg border border-black/10 p-4 dark:border-white/10">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Email ({rows.length})</h3>
        {canSend && enabled && (
          <button onClick={() => setComposing(true)} className="rounded bg-cobalt px-2.5 py-1 text-xs text-white">New email</button>
        )}
      </div>

      {error && <div className="mb-2 text-xs text-red-600">{error}</div>}
      {!enabled && <p className="text-sm opacity-60">The email channel needs a database migration before it can be used.</p>}

      {enabled && (loading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm opacity-60">No emails yet. Compose one to reach members or event registrants.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} className="rounded bg-black/5 p-2 text-sm dark:bg-white/5">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium">{r.subject}</span>
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: (STATUS_SURFACE[r.status] || STATUS_SURFACE.draft)[0],
                    color: (STATUS_SURFACE[r.status] || STATUS_SURFACE.draft)[1],
                  }}
                >
                  {EMAIL_STATUS_LABELS[r.status] || r.status}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs opacity-70">
                <span>{r.stats.total} recipients</span>
                {r.stats.sent > 0 && <span>{r.stats.sent} sent</span>}
                {r.stats.opened > 0 && <span>{r.stats.opened} opened</span>}
                {r.stats.clicked > 0 && <span>{r.stats.clicked} clicked</span>}
                {canSend && r.status === 'draft' && (
                  <button onClick={() => queue(r.id)} className="ml-auto rounded bg-cobalt px-2 py-0.5 text-white">Send now</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ))}

      {composing && (
        <EmailComposer
          tenantId={tenantId}
          campaignId={campaignId}
          eventId={eventId}
          onClose={() => setComposing(false)}
          onSaved={() => { setComposing(false); load(); }}
        />
      )}
    </section>
  );
}

function EmailComposer({ tenantId, campaignId, eventId, onClose, onSaved }) {
  const [form, setForm] = useState({
    subject: '', headline: '', bodyText: '', ctaLabel: '', ctaUrl: '',
    audienceType: eventId ? 'event_registrants' : 'members',
    scheduledAt: '',
  });
  const [audienceCount, setAudienceCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const audience = () => {
    const a = { type: form.audienceType };
    const spec = EMAIL_AUDIENCES.find((x) => x.value === form.audienceType);
    if (spec?.needsEvent && eventId) a.eventId = eventId;
    return a;
  };

  const checkAudience = async () => {
    setError('');
    try {
      const res = await previewAudience(tenantId, campaignId, audience());
      setAudienceCount(res.count);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not size the audience');
    }
  };

  const save = async (queueNow) => {
    if (!form.subject.trim() || !form.bodyText.trim()) { setError('Subject and body are required'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await createCampaignEmail(tenantId, campaignId, {
        subject: form.subject.trim(),
        headline: form.headline.trim() || undefined,
        bodyText: form.bodyText.trim(),
        ctaLabel: form.ctaLabel.trim() || undefined,
        ctaUrl: form.ctaUrl.trim() || undefined,
        audience: audience(),
        scheduledAt: form.scheduledAt || undefined,
      });
      if (queueNow && res.data?.id && !form.scheduledAt) {
        await queueCampaignEmail(tenantId, res.data.id);
      }
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-lg space-y-3 overflow-y-auto rounded-xl bg-white p-5 text-black dark:bg-neutral-900 dark:text-white" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold">New campaign email</h3>
        {error && <div className="text-sm text-red-600">{error}</div>}

        <input className={field} placeholder="Subject" value={form.subject} onChange={(e) => set('subject', e.target.value)} />
        <input className={field} placeholder="Headline (optional)" value={form.headline} onChange={(e) => set('headline', e.target.value)} />
        <textarea className={field} rows={5} placeholder="Body — plain text, one paragraph per line" value={form.bodyText} onChange={(e) => set('bodyText', e.target.value)} />
        <div className="flex gap-2">
          <input className={field} placeholder="Button label (optional)" value={form.ctaLabel} onChange={(e) => set('ctaLabel', e.target.value)} />
          <input className={field} placeholder="Button URL" value={form.ctaUrl} onChange={(e) => set('ctaUrl', e.target.value)} />
        </div>

        <label className="block text-xs opacity-70">Audience</label>
        <div className="flex gap-2">
          <select className={field} value={form.audienceType} onChange={(e) => { set('audienceType', e.target.value); setAudienceCount(null); }}>
            {EMAIL_AUDIENCES.filter((a) => !a.needsEvent || eventId).map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          <button type="button" onClick={checkAudience} className="whitespace-nowrap rounded border border-black/15 px-2 py-1.5 text-sm dark:border-white/15">
            Count
          </button>
        </div>
        {audienceCount != null && <p className="text-xs opacity-70">{audienceCount} reachable address{audienceCount === 1 ? '' : 'es'}</p>}

        <label className="block text-xs opacity-70">Schedule (optional — blank = send when you click Send)</label>
        <input type="datetime-local" className={field} value={form.scheduledAt} onChange={(e) => set('scheduledAt', e.target.value)} />

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Cancel</button>
          <button onClick={() => save(false)} disabled={busy} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15 disabled:opacity-50">
            Save draft
          </button>
          <button onClick={() => save(true)} disabled={busy} className="rounded bg-cobalt px-3 py-1.5 text-sm text-white disabled:opacity-50">
            {busy ? 'Saving…' : (form.scheduledAt ? 'Schedule' : 'Save & send')}
          </button>
        </div>
      </div>
    </div>
  );
}
